import React, { useState } from "react";
import { ProjectDocumentTreeFolderRowChrome } from "@sudowrite/shared-chat";
import { DocumentListActionMenu } from "../../DocumentListActionMenu/index.tsx";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { useProjectStore } from "../../../../stores/projectStore";
import { MAX_DOCUMENT_TITLE_LENGTH } from "../../../../../../src/shared/constants";
import classNames from "classnames";
import { isBatchDropDisabled, type TreeNode } from "../treeMultiSelectUtils";

interface Folder {
  id: string;
  name: string;
  type?: string;
}

interface DraggingNode {
  id: string;
  type: string;
}

interface DroppableFolderProps {
  folderId: string;
  folder: Folder | null;
  collapsed: boolean;
  toggleCollapsed: (e: React.MouseEvent | React.KeyboardEvent) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  nestingLevel: number;
  draggingNode: DraggingNode | null;
  firstDisplayOrder: number;
  isSelected: boolean;
  ariaSelected: boolean;
  onSelect: (e: React.MouseEvent, nodeId: string) => void;
  dragPayloadIds: string[];
  nodesById: Record<string, TreeNode | undefined>;
  onKeyboardMove?: (nodeId: string, direction: "up" | "down" | "left" | "right") => void;
}

export const DroppableFolder = ({
  folderId,
  folder,
  collapsed,
  toggleCollapsed,
  inputRef,
  nestingLevel,
  draggingNode,
  firstDisplayOrder,
  isSelected,
  ariaSelected,
  onSelect,
  dragPayloadIds,
  nodesById,
  onKeyboardMove,
}: DroppableFolderProps) => {
  const project = useProjectStore((state: any) => state.project);
  const tooManyLevels = nestingLevel >= 19;
  const isDescendant =
    draggingNode &&
    draggingNode.type === "folder" &&
    project.getDirectAncestors(folderId).includes(draggingNode.id);
  const batchDisableDroppable =
    dragPayloadIds.length > 1 &&
    isBatchDropDisabled({
      selectedIds: dragPayloadIds,
      destinationParentId: folderId,
      nestingLevel,
      nodesById,
    });
  const disableDroppable = tooManyLevels || isDescendant || batchDisableDroppable;

  const { setNodeRef, isOver, active } = useDroppable({
    id: folderId,
    data: { dropType: "folder", parentId: folderId, displayOrder: firstDisplayOrder },
    disabled: disableDroppable,
  });

  return (
    <div
      className={classNames("tree-display-folder", { isOver, notDragging: active === null })}
      ref={setNodeRef}
    >
      {folder ? (
        <FolderListItem
          folder={folder}
          inputRef={inputRef}
          isDropTarget={isOver}
          isSelected={isSelected}
          ariaSelected={ariaSelected}
          collapsed={collapsed}
          nestingLevel={nestingLevel}
          onSelect={onSelect}
          onToggleCollapsed={toggleCollapsed}
          onKeyboardMove={onKeyboardMove}
          dataNodeId={folderId}
        />
      ) : null}
    </div>
  );
};

interface FolderListItemProps {
  folder: Folder;
  inputRef: React.RefObject<HTMLInputElement>;
  isDropTarget?: boolean;
  isSelected?: boolean;
  ariaSelected?: boolean;
  collapsed?: boolean;
  nestingLevel?: number;
  onSelect?: (e: React.MouseEvent, nodeId: string) => void;
  onToggleCollapsed?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  onKeyboardMove?: (nodeId: string, direction: "up" | "down" | "left" | "right") => void;
  dataNodeId?: string;
}

export const FolderListItem = ({
  folder,
  inputRef,
  isDropTarget = false,
  isSelected = false,
  ariaSelected = false,
  collapsed = false,
  nestingLevel = 0,
  onSelect,
  onToggleCollapsed,
  onKeyboardMove,
  dataNodeId,
}: FolderListItemProps) => {
  const [newName, setNewName] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const setEditingTitle = useProjectStore((state: any) => state.setEditingTitle);
  const editingTitle = useProjectStore((state: any) => state.editingTitle);
  const project = useProjectStore((state: any) => state.project);

  const { listeners, isDragging, setNodeRef: setDraggableNodeRef, attributes: draggableAttributes } = useDraggable({
    id: folder.id,
    data: { nodeType: "folder" },
  });

  const updateFolderName = async (id: string, name: string) => {
    // Skip the save (and its full project re-fetch) when nothing changed —
    // merely entering and leaving rename mode should not reload the tree.
    if (editingTitle && name && name !== folder.name) {
      await project.updateFolder(id, { name });
    }
    setEditingTitle(null);
  };

  const isEditing = editingTitle === folder.id;

  const handleRowClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      onSelect?.(e, folder.id);
      return;
    }
    if (e.detail > 1) {
      return;
    }
    onToggleCollapsed?.(e);
  };

  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.altKey && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      const directionMap: Record<string, "up" | "down" | "left" | "right"> = {
        ArrowUp: "up",
        ArrowDown: "down",
        ArrowLeft: "left",
        ArrowRight: "right",
      };
      onKeyboardMove?.(folder.id, directionMap[e.key]);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggleCollapsed?.(e);
    }
  };

  // Rendered inside the shared row so renaming keeps the row's indent and icon.
  const editingContent = (
    <form
      className="folder-list-item-editing"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        const input = (e.target as HTMLFormElement).querySelector("input");
        updateFolderName(editingTitle, input?.value ?? newName);
      }}
    >
      <label className="sr-only" htmlFor={`folder-name-${folder.id}`}>
        Folder name
      </label>
      <input
        id={`folder-name-${folder.id}`}
        maxLength={MAX_DOCUMENT_TITLE_LENGTH}
        autoFocus
        ref={inputRef}
        type="text"
        defaultValue={newName}
        onBlur={(e) => {
          updateFolderName(editingTitle, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setEditingTitle(null);
          }
          e.stopPropagation();
        }}
      />
    </form>
  );

  return (
    <ProjectDocumentTreeFolderRowChrome
      name={folder.name}
      depth={nestingLevel}
      controlsVisibility="hover"
      isExpanded={!collapsed}
      isSelected={isSelected}
      isAriaSelected={ariaSelected}
      dataNodeId={dataNodeId ?? folder.id}
      rowRef={setDraggableNodeRef}
      tabIndex={isEditing ? -1 : 0}
      opacity={isDragging ? 0.5 : 1}
      actionsVisible={isMenuOpen}
      isEditing={isEditing}
      editingContent={editingContent}
      className={classNames("project-tree-folder-row", "draggable", {
        "is-selected": isSelected,
        "is-drop-target": isDropTarget,
      })}
      dragListeners={isEditing ? undefined : listeners}
      dragAttributes={isEditing ? undefined : draggableAttributes}
      onClick={isEditing ? undefined : handleRowClick}
      onDoubleClick={() => {
        setEditingTitle(folder.id);
        setNewName(folder.name);
      }}
      onKeyDown={isEditing ? undefined : handleRowKeyDown}
      onChevronClick={(e) => onToggleCollapsed?.(e)}
      renderActions={
        isEditing ? undefined : (
          <DocumentListActionMenu
            item={folder}
            setNewTitle={setNewName}
            onOpenChange={setIsMenuOpen}
          />
        )
      }
    />
  );
};
